import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  addManualTransaction, MONEY_OUT_CATEGORIES, MONEY_IN_CATEGORIES, PAYMENT_METHODS,
} from "@/lib/financialLedger";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
  instructors: Array<{ id: string; name: string }>;
  classes: Array<{ slug: string; title: string }>;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export function AddTransactionDialog({ open, onOpenChange, onSaved, instructors, classes }: Props) {
  const { toast } = useToast();
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [category, setCategory] = useState<string>("Expense");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [payee, setPayee] = useState("");
  const [method, setMethod] = useState<string>("zelle");
  const [classSlug, setClassSlug] = useState<string>("none");
  const [instructorId, setInstructorId] = useState<string>("none");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [reference, setReference] = useState("");
  const [recurrence, setRecurrence] = useState<"one_time" | "recurring">("one_time");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const categories = direction === "out" ? MONEY_OUT_CATEGORIES : MONEY_IN_CATEGORIES;

  const reset = () => {
    setDirection("out"); setCategory("Expense"); setAmount(""); setDate(todayISO());
    setPayee(""); setMethod("zelle"); setClassSlug("none"); setInstructorId("none");
    setReceiptUrl(""); setReference(""); setRecurrence("one_time"); setDescription(""); setNotes("");
  };

  const save = async () => {
    const cents = Math.round(Number(amount.replace(/[^0-9.\-]/g, "")) * 100);
    if (!cents || cents <= 0) {
      toast({ title: "Enter an amount", description: "The amount must be greater than zero.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await addManualTransaction({
        direction, category, amount_cents: cents, transaction_date: date,
        description: description || category,
        payment_method: method, counterparty_name: payee || null,
        related_class_id: classSlug === "none" ? null : classSlug,
        related_class_title: classSlug === "none" ? null : classes.find((c) => c.slug === classSlug)?.title ?? null,
        related_instructor_id: instructorId === "none" ? null : instructorId,
        receipt_url: receiptUrl || null,
        reference_number: reference || null,
        recurrence, notes: notes || null,
      });
      toast({ title: "Transaction recorded" });
      reset();
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast({ title: "Could not save", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Transaction</DialogTitle>
          <DialogDescription>
            Record money that moved outside of registrations and payroll — those post automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={(v) => { setDirection(v as any); setCategory(v === "out" ? "Expense" : "Other Income"); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="out">Money Out</SelectItem>
                <SelectItem value="in">Money In</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ledger-amount">Amount</Label>
            <Input id="ledger-amount" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ledger-date">Date</Label>
            <Input id="ledger-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ledger-payee">Vendor or payee</Label>
            <Input id="ledger-payee" value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Who was paid" />
          </div>

          <div className="space-y-1.5">
            <Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Related class (optional)</Label>
            <Select value={classSlug} onValueChange={setClassSlug}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Related instructor (optional)</Label>
            <Select value={instructorId} onValueChange={setInstructorId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ledger-reference">Reference number (optional)</Label>
            <Input id="ledger-reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Recurring or one-time</Label>
            <Select value={recurrence} onValueChange={(v) => setRecurrence(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="one_time">One-time</SelectItem>
                <SelectItem value="recurring">Recurring</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ledger-desc">Description</Label>
            <Input id="ledger-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this was for" />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ledger-receipt">Receipt or invoice link (optional)</Label>
            <Input id="ledger-receipt" value={receiptUrl} onChange={(e) => setReceiptUrl(e.target.value)} placeholder="https://…" />
            <p className="text-xs text-muted-foreground">
              Leave blank to save now — the transaction will be flagged as missing documentation.
            </p>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ledger-notes">Notes</Label>
            <Textarea id="ledger-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save transaction"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
